<script setup lang="ts">
import { ref, onMounted, inject, Ref, computed, watch } from 'vue'
import { useTransfers } from '@/services/Transfers'
import { Member } from '@/types/Member'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid'
import { RefreshCw, CheckCircle2, XCircle, Clock, Loader2, Download, Trash2 } from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import axios from 'axios'

const {
	transferProfiles,
	transferQueue,
	transferStats,
	loading,
	error,
	getTransferProfiles,
	getTransferQueue,
	getTransferStats,
	retryTransfer,
	deleteTransferJob,
} = useTransfers()

const vhostId = inject<Ref<string>>('vhostId', ref('0'))
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const selectedTab = ref('queue')
const statusFilter = ref('all')
const waitingShow = ref(5)
const inProgressShow = ref(5)

// Summary tab state
const reportStartDate = ref(new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0])
const reportEndDate = ref(new Date().toISOString().split('T')[0])
const reportStatus = ref('')
const transferSummary = ref<any[]>([])
const summaryLoading = ref(false)

// Split queue into sections like the legacy app
const queuedTransfers = computed(() =>
	transferQueue.value.filter((i) => i.status === 'queued' || i.status === 'assigned')
)
const inProgressTransfers = computed(() =>
	transferQueue.value.filter((i) => i.status === 'transferring' || i.status === 'processing')
)
const completedTransfers = computed(() =>
	transferQueue.value.filter((i) => i.status !== 'queued' && i.status !== 'assigned' && i.status !== 'transferring' && i.status !== 'processing')
)

const getStatusIcon = (status: string) => {
	if (status === 'success' || status === 'completed') return CheckCircle2
	if (status === 'failure' || status === 'failed') return XCircle
	if (status === 'transferring' || status === 'processing') return Loader2
	return Clock
}

const getStatusVariant = (status: string): 'default' | 'destructive' | 'secondary' | 'outline' => {
	if (status === 'success' || status === 'completed') return 'default'
	if (status === 'failure' || status === 'failed') return 'destructive'
	if (status === 'transferring' || status === 'processing') return 'secondary'
	return 'outline'
}

const handleRetry = async (mediaId: number, transferProfileId: number) => {
	try {
		const response = await axios.post('/services/json', {
			method: 'media.startClientTransfers',
			mid: mediaId,
			tid: transferProfileId,
		})
		if (response.data.status !== false) {
			toast('Requeued', { description: `Media ${mediaId} requeued for transfer.` })
			await refreshData()
		} else {
			toast('Error', { description: 'Failed to requeue transfer.' })
		}
	} catch {
		toast('Error', { description: 'Failed to requeue transfer.' })
	}
}

const handleDelete = async (jobId: number) => {
	const success = await deleteTransferJob(vhostId.value, jobId)
	if (success) {
		toast('Removed', { description: 'Transfer job removed.' })
		await refreshData()
	} else {
		toast('Error', { description: 'Failed to remove transfer job.' })
	}
}

const refreshData = async () => {
	if (vhostId.value) {
		await Promise.all([
			getTransferProfiles(vhostId.value),
			getTransferQueue(vhostId.value),
		])
	}
}

const fetchSummary = async () => {
	summaryLoading.value = true
	try {
		const response = await axios.post('/services/json', {
			method: 'media.getTransferSummary',
			vhostID: vhostId.value,
			startDate: reportStartDate.value,
			endDate: reportEndDate.value,
		})
		if (response.data.status !== false) {
			const result = response.data.result
			transferSummary.value = Array.isArray(result) ? result : (result?.data || [])
		}
	} catch (err) {
		console.error('Error fetching transfer summary:', err)
	} finally {
		summaryLoading.value = false
	}
}

const getCSVReport = async () => {
	reportStatus.value = 'Generating...'
	try {
		const response = await axios.post('/services/json', {
			method: 'media.getTransferReport',
			vhostID: vhostId.value,
			startDate: reportStartDate.value,
			endDate: reportEndDate.value,
			format: 'csv',
		})
		if (response.data.status !== false && response.data.result) {
			const blob = new Blob([response.data.result], { type: 'text/csv' })
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = `transfer-report-${reportStartDate.value}-${reportEndDate.value}.csv`
			a.click()
			URL.revokeObjectURL(url)
			reportStatus.value = ''
		} else {
			reportStatus.value = 'No data'
			setTimeout(() => { reportStatus.value = '' }, 3000)
		}
	} catch {
		reportStatus.value = 'Error'
		setTimeout(() => { reportStatus.value = '' }, 3000)
	}
}

onMounted(() => {
	refreshData()
})

watch(selectedTab, (tab) => {
	if (tab === 'summary' && transferSummary.value.length === 0) {
		fetchSummary()
	}
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="flex justify-end">
			<Button variant="outline" @click="refreshData">
				<RefreshCw class="h-4 w-4 mr-2" />
				Refresh
			</Button>
		</div>

		<div class="grid gap-4 md:grid-cols-4">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Total</CardTitle>
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ transferStats.totalTransfers }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Waiting</CardTitle>
					<Clock class="h-5 w-5 text-yellow-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ queuedTransfers.length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">In Progress</CardTitle>
					<Loader2 class="h-5 w-5 text-blue-500 animate-spin" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ inProgressTransfers.length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Failed</CardTitle>
					<XCircle class="h-5 w-5 text-red-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ transferStats.failedCount }}</div>
				</CardContent>
			</Card>
		</div>

		<Tabs v-model="selectedTab" class="space-y-4">
			<TabsList>
				<TabsTrigger value="queue">Standard</TabsTrigger>
				<TabsTrigger value="summary">Summary</TabsTrigger>
				<TabsTrigger value="profiles">Profiles</TabsTrigger>
			</TabsList>

			<!-- Standard Queue Tab -->
			<TabsContent value="queue" class="space-y-6">
				<!-- Waiting to be published -->
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-light">Waiting to be Published</CardTitle>
						<CardDescription>{{ queuedTransfers.length }} items queued</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Media ID</TableHead>
									<TableHead>Media Info</TableHead>
									<TableHead>Publish At</TableHead>
									<TableHead>Transfer</TableHead>
									<TableHead>Attempts</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Queue Time</TableHead>
									<TableHead class="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-if="queuedTransfers.length > 0">
									<TableRow v-for="item in queuedTransfers.slice(0, waitingShow)" :key="item.id" class="border-l-2 border-l-yellow-400">
										<TableCell>
											<router-link :to="'/media/' + item.mediaId" class="text-primary hover:underline font-medium">{{ item.mediaId }}</router-link>
										</TableCell>
										<TableCell class="text-sm">{{ item.mediaTitle }}</TableCell>
										<TableCell class="text-sm text-muted-foreground">{{ item.publishAt || 'Immediately' }}</TableCell>
										<TableCell class="text-sm">{{ item.transferProfileName }}</TableCell>
										<TableCell class="text-sm">{{ item.attempts }}</TableCell>
										<TableCell>
											<Badge :variant="getStatusVariant(item.status)" class="capitalize">{{ item.status }}</Badge>
										</TableCell>
										<TableCell class="text-sm text-muted-foreground">{{ item.createdDate }}</TableCell>
										<TableCell class="text-right">
											<div class="flex justify-end gap-1">
												<Button variant="outline" size="sm" @click="handleRetry(item.mediaId, item.transferProfileId)">Requeue</Button>
												<Button variant="ghost" size="sm" @click="handleDelete(item.id)"><Trash2 class="h-3 w-3 text-destructive" /></Button>
											</div>
										</TableCell>
									</TableRow>
								</template>
								<TableRow v-else>
									<TableCell colspan="8" class="h-16 text-center text-muted-foreground">No items waiting.</TableCell>
								</TableRow>
								<TableRow v-if="queuedTransfers.length > waitingShow">
									<TableCell colspan="8" class="text-right">
										<Button variant="outline" size="sm" @click="waitingShow = 1000">Show More</Button>
									</TableCell>
								</TableRow>
								<TableRow v-else-if="waitingShow > 5 && queuedTransfers.length > 5">
									<TableCell colspan="8" class="text-right">
										<Button variant="outline" size="sm" @click="waitingShow = 5">Show Less</Button>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</CardContent>
				</Card>

				<!-- In Progress -->
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-light">In Progress</CardTitle>
						<CardDescription>{{ inProgressTransfers.length }} items transferring</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Media ID</TableHead>
									<TableHead>Media Info</TableHead>
									<TableHead>Transfer</TableHead>
									<TableHead>Attempts</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Queue Time</TableHead>
									<TableHead class="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-if="inProgressTransfers.length > 0">
									<TableRow v-for="item in inProgressTransfers.slice(0, inProgressShow)" :key="item.id" class="border-l-2 border-l-blue-400">
										<TableCell>
											<router-link :to="'/media/' + item.mediaId" class="text-primary hover:underline font-medium">{{ item.mediaId }}</router-link>
										</TableCell>
										<TableCell class="text-sm">{{ item.mediaTitle }}</TableCell>
										<TableCell class="text-sm">{{ item.transferProfileName }}</TableCell>
										<TableCell class="text-sm">{{ item.attempts }}</TableCell>
										<TableCell>
											<Badge variant="secondary"><Loader2 class="h-3 w-3 mr-1 animate-spin" />Transferring</Badge>
										</TableCell>
										<TableCell class="text-sm text-muted-foreground">{{ item.createdDate }}</TableCell>
										<TableCell class="text-right">
											<div class="flex justify-end gap-1">
												<Button variant="outline" size="sm" @click="handleRetry(item.mediaId, item.transferProfileId)">Requeue</Button>
												<Button variant="ghost" size="sm" @click="handleDelete(item.id)"><Trash2 class="h-3 w-3 text-destructive" /></Button>
											</div>
										</TableCell>
									</TableRow>
								</template>
								<TableRow v-else>
									<TableCell colspan="7" class="h-16 text-center text-muted-foreground">No transfers in progress.</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</CardContent>
				</Card>

				<!-- Completed -->
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-light">Completed</CardTitle>
						<CardDescription>{{ completedTransfers.length }} items completed</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Media ID</TableHead>
									<TableHead>Media Info</TableHead>
									<TableHead>Transfer</TableHead>
									<TableHead>Attempts</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Queue Time</TableHead>
									<TableHead class="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-if="completedTransfers.length > 0">
									<TableRow v-for="item in completedTransfers" :key="item.id" :class="item.status === 'failure' ? 'border-l-2 border-l-red-400' : 'border-l-2 border-l-green-400'">
										<TableCell>
											<router-link :to="'/media/' + item.mediaId" class="text-primary hover:underline font-medium">{{ item.mediaId }}</router-link>
										</TableCell>
										<TableCell class="text-sm">{{ item.mediaTitle }}</TableCell>
										<TableCell class="text-sm">{{ item.transferProfileName }}</TableCell>
										<TableCell class="text-sm">{{ item.attempts }}</TableCell>
										<TableCell>
											<Badge :variant="getStatusVariant(item.status)" class="capitalize">
												<component :is="getStatusIcon(item.status)" class="h-3 w-3 mr-1" />
												{{ item.status }}
											</Badge>
										</TableCell>
										<TableCell class="text-sm text-muted-foreground">{{ item.createdDate }}</TableCell>
										<TableCell class="text-right">
											<div class="flex justify-end gap-1">
												<Button variant="outline" size="sm" @click="handleRetry(item.mediaId, item.transferProfileId)">Requeue</Button>
												<Button variant="ghost" size="sm" @click="handleDelete(item.id)"><Trash2 class="h-3 w-3 text-destructive" /></Button>
											</div>
										</TableCell>
									</TableRow>
								</template>
								<TableRow v-else>
									<TableCell colspan="7" class="h-16 text-center text-muted-foreground">
										<div v-if="loading">Loading...</div>
										<div v-else>No completed transfers.</div>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>

			<!-- Summary Tab -->
			<TabsContent value="summary">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-light">Transfer Summary</CardTitle>
						<CardDescription>View transfer details by date range and download reports</CardDescription>
					</CardHeader>
					<CardContent class="space-y-4">
						<div class="flex flex-wrap items-end gap-4">
							<div class="space-y-1">
								<Label class="text-xs">Start Date</Label>
								<Input type="date" v-model="reportStartDate" class="w-40 h-9" />
							</div>
							<div class="space-y-1">
								<Label class="text-xs">End Date</Label>
								<Input type="date" v-model="reportEndDate" class="w-40 h-9" />
							</div>
							<Button @click="fetchSummary" :disabled="summaryLoading">
								<RefreshCw class="h-4 w-4 mr-1" :class="{'animate-spin': summaryLoading}" /> Load
							</Button>
							<Button variant="outline" @click="getCSVReport" :disabled="reportStatus !== ''">
								<Download class="h-4 w-4 mr-1" />
								{{ reportStatus || 'Download CSV' }}
							</Button>
						</div>

						<div v-if="transferSummary.length > 0" class="space-y-2">
							<div class="grid grid-cols-12 gap-2 border-b pb-2 text-xs font-medium text-muted-foreground uppercase">
								<div class="col-span-4">Details</div>
								<div class="col-span-3">Destinations</div>
								<div class="col-span-5">Related Items</div>
							</div>
							<div v-for="(item, index) in transferSummary" :key="index" class="grid grid-cols-12 gap-2 border rounded p-3 hover:bg-muted/30">
								<div class="col-span-4">
									<router-link :to="'/media/' + item.mid" class="text-primary hover:underline font-medium text-sm">{{ item.title }}</router-link>
									<p class="text-xs text-muted-foreground mt-0.5">{{ item.filename }}</p>
								</div>
								<div class="col-span-3">
									<div v-for="(transfer, tIdx) in (item.transfers || [])" :key="tIdx" class="text-sm mb-1">
										<span>{{ transfer.destination }}</span>
										<span class="text-xs text-muted-foreground ml-1">{{ transfer.queued_at }}</span>
										<CheckCircle2 v-if="transfer.status === 'success'" class="h-3.5 w-3.5 inline ml-1 text-green-500" />
										<XCircle v-else class="h-3.5 w-3.5 inline ml-1 text-red-500" />
									</div>
								</div>
								<div class="col-span-5">
									<div v-for="(child, cIdx) in (item.children || [])" :key="cIdx" class="text-sm mb-1">
										<span>{{ child.title }}</span>
										<CheckCircle2 v-if="child.status === 'success'" class="h-3.5 w-3.5 inline ml-1 text-green-500" />
										<XCircle v-else class="h-3.5 w-3.5 inline ml-1 text-red-500" />
									</div>
									<span v-if="!item.children?.length" class="text-xs text-muted-foreground">—</span>
								</div>
							</div>
						</div>
						<div v-else-if="summaryLoading" class="py-8 text-center text-muted-foreground">Loading summary...</div>
						<div v-else class="py-8 text-center text-muted-foreground">No summary data. Select a date range and click Load.</div>
					</CardContent>
				</Card>
			</TabsContent>

			<!-- Profiles Tab -->
			<TabsContent value="profiles">
				<Card>
					<CardHeader>
						<div class="flex justify-between items-center">
							<div>
								<CardTitle class="text-lg font-light">Transfer Profiles</CardTitle>
								<CardDescription>Configure your content distribution destinations</CardDescription>
							</div>
							<Button>Add Profile</Button>
						</div>
					</CardHeader>
					<CardContent>
						<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
							<Card v-for="profile in transferProfiles" :key="profile.id" class="hover:shadow-md transition-shadow">
								<CardHeader>
									<div class="flex justify-between items-start">
										<div>
											<CardTitle class="text-base">{{ profile.name }}</CardTitle>
											<CardDescription>{{ profile.type }}</CardDescription>
										</div>
										<Badge :variant="profile.status === 'active' ? 'default' : 'secondary'">{{ profile.status }}</Badge>
									</div>
								</CardHeader>
								<CardContent>
									<div class="text-sm text-muted-foreground">
										<div>Destination: {{ profile.destination }}</div>
										<div v-if="profile.lastTransferDate">Last transfer: {{ profile.lastTransferDate }}</div>
									</div>
									<div class="flex space-x-2 mt-3">
										<Button variant="outline" size="sm">Edit</Button>
										<Button variant="outline" size="sm">Test</Button>
									</div>
								</CardContent>
							</Card>
							<Card v-if="transferProfiles.length === 0" class="col-span-full">
								<CardContent class="h-32 flex items-center justify-center text-muted-foreground">
									<div v-if="loading">Loading profiles...</div>
									<div v-else>No transfer profiles configured.</div>
								</CardContent>
							</Card>
						</div>
					</CardContent>
				</Card>
			</TabsContent>
		</Tabs>
	</div>
</template>
